import { Component, ElementRef, HostListener, Input, OnInit, ViewChild, HostBinding } from '@angular/core';
import { HasSubscriptions } from '../has-subscriptions';
import { Router } from '@angular/router';
import { CommunicationService } from '../../services/communication.service';
import { PreferencesService } from '../../services/preferences.service';
import { SCREEN_SIZE, ListingCategory, MyRemaxModes } from '../../classes/enums';
import { UserService } from '../../services/user.service';
import { User } from '../../classes/user';
import { CredentialsService } from '../../services/credentials.service';
import { StaticPathPipe } from '../../shared/pipes/static-path.pipe';
import { Header, Img } from '../../classes/interfaces';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: [
    './header.component.scss',
    './header.component.desktop.scss'
  ]
})
export class HeaderComponent extends HasSubscriptions implements OnInit {
  @Input() currentPage: string;
  @Input() currentShownListingType: ListingCategory;
  @ViewChild('myRemaxMenu') myRemaxMenu: ElementRef;
  @ViewChild('myAccount') myAccount: ElementRef;
  totalFavourites: number = 0;
  screenSize: number;
  showMyRemaxMenu: boolean = false;
  currentUser: User;
  isAuthenticated: boolean = false;
  activeMyRemaxMode: MyRemaxModes = MyRemaxModes.FAVOURITES; // Saved searches or Favourites Tab specifically for MyRemax Page

  logos: { [key: number]: { n: string; w: string } } = {
    [ListingCategory.RESIDENTIAL]: { n: 'REMAX_masterLogo@1x.svg', w: 'REMAX_masterLogo_white@1x.svg' },
    [ListingCategory.LUXURY]: { n: 'REMAX_Collection.svg', w: 'REMAX_Collection_white.svg' },
    [ListingCategory.COMMERCIAL]: { n: 'REMAX_Commercial.svg', w: 'REMAX_Commercial_white.svg' }
  };

  faveIcons: { [key: number]: { on: { n: string; w: string }, off: { n: string; w: string } } } = {
    [ListingCategory.RESIDENTIAL]: {
      on: { n: 'fave_icon_on_blue@2x.svg', w: 'fave_icon_on_white@2x.svg' },
      off: { n: 'fave_icon_off_blue@2x.svg', w: 'fave_icon_off_white@2x.svg' },
    },
    [ListingCategory.LUXURY]: {
      on: { n: 'fave_icon_on_blue@2x.svg', w: 'fave_icon_on_white@2x.svg' },
      off: { n: 'fave_icon_off_blue@2x.svg', w: 'fave_icon_off_white@2x.svg' }
    },
    [ListingCategory.COMMERCIAL]: {
      on: { n: 'fave_icon_on_gray@2x.svg', w: 'fave_icon_on_white@2x.svg' },
      off: { n: 'fave_icon_off_gray@2x.svg', w: 'fave_icon_off_white@2x.svg' }
    }
  };

  accountIcons: { [key: number]: { on: { n: string; w: string }, off: { n: string; w: string } } } = {
    [ListingCategory.RESIDENTIAL]: {
      on: { n: 'account_icon_on_blue@2x.svg', w: 'account_icon_on_white@2x.svg' },
      off: { n: 'account_icon_off_blue@2x.svg', w: 'account_icon_off_white@2x.svg' },
    },
    [ListingCategory.LUXURY]: {
      on: { n: 'account_icon_on_blue@2x.svg', w: 'account_icon_on_white@2x.svg' },
      off: { n: 'account_icon_off_blue@2x.svg', w: 'account_icon_off_white@2x.svg' },
    },
    [ListingCategory.COMMERCIAL]: {
      on: { n: 'account_icon_on_gray@2x.svg', w: 'account_icon_on_white@2x.svg' },
      off: { n: 'account_icon_off_gray@2x.svg', w: 'account_icon_off_white@2x.svg' },
    }
  };

  private _showLeftNav: boolean = false;
  private _showRightNav: boolean = false;

  assetDir = '/assets/icons/nav-bar/';

  header: Header = {
    residential: {
      className: '',
      img: { src: '', height: 0, width: 0 },
      link: '/'
    },
    commercial: {
      className: '',
      img: { src: '', height: 0, width: 0 },
      link: '/commercial'
    },
    luxury: {
      className: '',
      img: { src: '', height: 0, width: 0 },
      link: '/luxury'
    }
  };

  // Enum
  ListingCategory = ListingCategory;
  constructor(
    private communicationService: CommunicationService,
    private _preferencesService: PreferencesService,
    private userService: UserService,
    private router: Router,
    private credentialService: CredentialsService,
    private staticPathPipe: StaticPathPipe
  ) { super(); }

  ngOnInit() {
    this.addSub(this._preferencesService.favouriteListingsChanged.subscribe((value) => this.totalFavourites = value.length));
    this.addSub(this.communicationService.navigation.toggleNavs.subscribe((s) => {
      this._showLeftNav = s.left;
      this._showRightNav = s.right;
    }));
    this.addSub(this.communicationService.screenSize.pipe(debounceTime(50)).subscribe((screenSize) => {
      this.screenSize = screenSize;
      this.setState(this.currentShownListingType);
    }));
    this.addSub(this.userService.currentUser.subscribe((currentUser) => this.currentUser = currentUser));
    this.addSub(this.credentialService.isLoggedIn.subscribe((isAuthenticated) => this.isAuthenticated = isAuthenticated));
    this.addSub(this.communicationService.activeMyRemaxMode.subscribe((mode) => this.activeMyRemaxMode = mode));
    this.addSub(this.communicationService.listings.currentShownListingType.subscribe((listingType) => {
      this.setState(listingType);
    }));
  }

  @HostListener('window:click', ['$event'])
  hideMyRemaxMenu(event) {
    if (this.showMyRemaxMenu && !this.myAccount.nativeElement.contains(event.toElement)) {
      this.showMyRemaxMenu = false;
    }
  }

  toggleLeftNav() {
    this.communicationService.navigation.toggleNavs.next({ left: !this._showLeftNav, right: false });
  }

  toggleRightNav(event) {
    event.stopPropagation();
    if (this.isMobile) {
      this.communicationService.navigation.toggleNavs.next({ left: false, right: !this._showRightNav });
    } else {
      // show my remax menu
      this.showMyRemaxMenu = !this.showMyRemaxMenu;
    }
  }

  private get isMobile() {
    return this.screenSize < SCREEN_SIZE.DESKTOP_UP;
  }

  updateCurrentListingType(newListingType: ListingCategory) {
    this.communicationService.setCurrentListingType(newListingType, true);
    if (this.router.url.indexOf('/find-real-estate') < 0) {
      this.router.navigate(['/find-real-estate']);
    }
  }

  toHomePage(listingType: ListingCategory) {
    this.communicationService.setCurrentListingType(listingType, true);
  }

  openRegisterLogin() {
    this.router.navigate( ['/', { outlets: { popup: 'register-login'} }]);
  }

  async toMyRemax() {
    this._closeNavs();
    await this.router.navigate(['/my-remax']);
  }

  async toFindListings($event?) {
    if ($event) $event.preventDefault();
    this._closeNavs();
    await this.router.navigate(['/find-real-estate']);
  }

  private _closeNavs() {
    this.communicationService.navigation.toggleNavs.next({ left: false, right: false });
  }

  get faveIcon(): string {
    const isLuxury = this.currentShownListingType === ListingCategory.LUXURY;
    let icon = 'fave_icon_off@2x.svg';
    if (this.currentPage) {
      if (isLuxury) {
        icon = this.faveIcons[this.currentShownListingType].off.w;
      } else {
        icon = this.isMobile ? this.faveIcons[this.currentShownListingType].off.n : this.faveIcons[this.currentShownListingType].off.w;
      }
    }
    return this.staticPathPipe.transform(this.assetDir + icon);
  }

  get accountIcon(): string {
    const isLuxury = this.currentShownListingType === ListingCategory.LUXURY;
    let icon = 'account_icon_off@2x.svg';
    if (this.currentPage) {
      const isAccount = this.currentPage.indexOf('/my-account') >= 0;
      const onOrOff = isAccount ? 'on' : 'off';
      if (isLuxury) {
        icon = this.accountIcons[this.currentShownListingType][onOrOff].w;
      } else {
        icon = this.isMobile ? this.accountIcons[this.currentShownListingType][onOrOff].n : this.accountIcons[this.currentShownListingType][onOrOff].w;
      }
    }
    return this.staticPathPipe.transform(this.assetDir + icon);
  }

  SCREEN_SIZE = SCREEN_SIZE;

  setState(nextListingCategory: ListingCategory): void {
    this.header.residential.className = this.getLinkClass(ListingCategory.RESIDENTIAL, nextListingCategory);
    this.header.commercial.className = this.getLinkClass(ListingCategory.COMMERCIAL, nextListingCategory);
    this.header.luxury.className = this.getLinkClass(ListingCategory.LUXURY, nextListingCategory);

    this.header.residential.img.src = this.getLinkImg(ListingCategory.RESIDENTIAL, nextListingCategory);
    this.header.commercial.img.src = this.getLinkImg(ListingCategory.COMMERCIAL, nextListingCategory);
    this.header.luxury.img.src = this.getLinkImg(ListingCategory.LUXURY, nextListingCategory);
  }

  getLinkImg(listingCategory: ListingCategory, nextListingCategory: ListingCategory): string {
    return this.getLinkImgSrc(listingCategory, nextListingCategory)
  }

  getLinkClass(listingCategory: ListingCategory, nextListingCategory: ListingCategory): string {
    const type = ListingCategory[listingCategory];
    const active = ListingCategory[nextListingCategory];
    const shouldHide = nextListingCategory !== listingCategory ? 'hide-on-desktop-down' : '';
    return `link link-${type} active-${active} ${shouldHide}`;
  }

  getLinkImgSrc(listingCategory: ListingCategory, nextListingCategory: ListingCategory): string {
    const isLuxury = nextListingCategory === ListingCategory.LUXURY;
    const img = isLuxury ? this.logos[listingCategory]['w'] : this.logos[listingCategory]['n'];
    return this.staticPathPipe.transform(`/assets/REMAX_Logo/${img}`);
  }

  @HostBinding('class.luxury') get isLuxury() { return this.currentShownListingType === ListingCategory.LUXURY };
}

